import { useState, useRef, useCallback, useEffect } from "react";
import AttackGraph from "./AttackGraph";

// ─── DESIGN SYSTEM ────────────────────────────────────────────────────────────
// Dark command surface. Chroma is reserved almost entirely for severity, so the
// eye goes to danger and nowhere else. Scale and structure carry the rest.
const base = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --void: #0B0A0E; --panel: #141219; --panel-2: #1B1822; --sunk: #08070B;
  --line: #262230; --line-2: #35303F;
  --bone: #F2EDE6; --ash: #8F8799; --dim: #5C5567;
  --crit: #FF3B30; --high: #FF8A3D; --med: #E8C14A; --safe: #3DD68C;
  --crit-dim: rgba(255,59,48,0.12); --high-dim: rgba(255,138,61,0.12);
  --med-dim: rgba(232,193,74,0.12); --safe-dim: rgba(61,214,140,0.12);
  --display: 'Syne', sans-serif;
  --sans: 'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif;
  --mono: 'IBM Plex Mono', ui-monospace, monospace;
}
body { background: var(--void); color: var(--bone); font-family: var(--sans); min-height: 100vh; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
.veil { position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background: radial-gradient(ellipse 90% 50% at 50% -10%, rgba(143,135,153,0.10), transparent 70%); }
.app { min-height: 100vh; display: flex; flex-direction: column; position: relative; z-index: 1; }
main { flex: 1; }
.shell { max-width: 1240px; margin: 0 auto; padding-inline: 28px; width: 100%; }
.tnum { font-variant-numeric: tabular-nums; }

/* ---- nav ---- */
nav { border-bottom: 1px solid var(--line); height: 60px; display: flex; align-items: center;
  background: rgba(11,10,14,0.88); backdrop-filter: blur(16px); position: sticky; top: 0; z-index: 100; }
.nav-in { display: flex; align-items: center; justify-content: space-between; width: 100%; }
.brand { display: flex; align-items: center; gap: 11px; cursor: pointer; background: none; border: none; padding: 0; }
.brand-name { font-family: var(--display); font-weight: 800; font-size: 1.02rem; letter-spacing: -0.02em; color: var(--bone); }
.tabs { display: flex; gap: 2px; }
.tab { font-family: var(--mono); font-size: 0.72rem; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--ash); background: none; border: none; padding: 7px 13px; cursor: pointer; border-radius: 5px; transition: color .15s, background .15s; }
.tab:hover { color: var(--bone); background: var(--panel); }
.tab.on { color: var(--void); background: var(--bone); }
.pulse { display: flex; align-items: center; gap: 7px; font-family: var(--mono); font-size: 0.68rem; color: var(--ash); letter-spacing: 0.05em; }
.pulse i { width: 6px; height: 6px; border-radius: 50%; background: var(--safe); box-shadow: 0 0 0 3px var(--safe-dim); }

/* ---- shared surfaces (also consumed by AttackGraph.js) ---- */
.findings-panel { background: var(--panel); border: 1px solid var(--line); border-radius: 3px; overflow: hidden; }
.panel-header { padding: 13px 18px; border-bottom: 1px solid var(--line); display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.panel-title { font-family: var(--mono); font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.13em; color: var(--ash); }
.sev-badge { font-family: var(--mono); font-size: 0.58rem; font-weight: 500; padding: 3px 7px; border-radius: 2px;
  letter-spacing: 0.1em; text-transform: uppercase; flex-shrink: 0; }
.badge-CRITICAL { background: var(--crit-dim); color: var(--crit); }
.badge-HIGH { background: var(--high-dim); color: var(--high); }
.badge-MEDIUM { background: var(--med-dim); color: var(--med); }
.badge-LOW { background: var(--safe-dim); color: var(--safe); }
.finding-top { display: flex; align-items: baseline; gap: 11px; margin-bottom: 5px; }
.finding-title { font-size: 0.9rem; font-weight: 500; line-height: 1.4; color: var(--bone); }
`;

const upload = `
.stage { padding-block: clamp(48px, 9vw, 104px) 72px; }
.kicker { font-family: var(--mono); font-size: 0.68rem; letter-spacing: 0.18em; text-transform: uppercase;
  color: var(--ash); display: flex; align-items: center; gap: 11px; margin-bottom: 26px; }
.kicker::before { content: ''; width: 26px; height: 1px; background: var(--line-2); }
.headline { font-family: var(--display); font-weight: 800; font-size: clamp(2.6rem, 8.5vw, 6.4rem);
  line-height: 0.92; letter-spacing: -0.035em; margin-bottom: 22px; max-width: 14ch; text-wrap: balance; }
.headline em { font-style: normal; color: var(--dim); }
.deck { font-size: 1rem; color: var(--ash); max-width: 56ch; line-height: 1.65; margin-bottom: 46px; }
.metrics { display: flex; flex-wrap: wrap; gap: 0; border-top: 1px solid var(--line); margin-bottom: 44px; }
.metric { padding: 18px 30px 18px 0; margin-right: 30px; border-right: 1px solid var(--line); }
.metric:last-child { border-right: none; }
.metric-n { font-family: var(--display); font-weight: 700; font-size: 1.7rem; letter-spacing: -0.02em; line-height: 1; }
.metric-l { font-family: var(--mono); font-size: 0.64rem; text-transform: uppercase; letter-spacing: 0.11em; color: var(--dim); margin-top: 7px; }

.slab { border: 1px solid var(--line); background: var(--panel); border-radius: 3px; position: relative; overflow: hidden;
  display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 26px; padding: 30px 30px; cursor: pointer; transition: border-color .2s, background .2s; }
.slab:hover, .slab.over { border-color: var(--line-2); background: var(--panel-2); }
.slab.over { border-style: dashed; }
.slab-lead { font-family: var(--display); font-weight: 700; font-size: 1.32rem; letter-spacing: -0.02em; margin-bottom: 7px; }
.slab-sub { font-family: var(--mono); font-size: 0.73rem; color: var(--dim); }
.slab-file { font-family: var(--mono); font-size: 0.73rem; color: var(--safe); margin-top: 11px; display: flex; align-items: center; gap: 8px; }
.go { font-family: var(--sans); font-weight: 600; font-size: 0.86rem; letter-spacing: 0.02em;
  background: var(--bone); color: var(--void); border: none; padding: 14px 28px; border-radius: 3px; cursor: pointer; white-space: nowrap; transition: opacity .15s, transform .15s; }
.go:hover:not(:disabled) { transform: translateX(3px); }
.go:disabled { background: var(--panel-2); color: var(--dim); cursor: not-allowed; }
.scanning { position: absolute; inset: 0; background: rgba(8,7,11,0.96); display: flex; flex-direction: column; justify-content: center; padding: 30px; }
.scan-rail { height: 1px; background: var(--line); margin-bottom: 22px; overflow: hidden; }
.scan-rail i { display: block; height: 100%; width: 34%; background: var(--bone); animation: sweep 1.3s cubic-bezier(.6,0,.4,1) infinite; }
@keyframes sweep { 0% { transform: translateX(-100%); } 100% { transform: translateX(394%); } }
.scan-step { font-family: var(--mono); font-size: 0.8rem; color: var(--bone); }
.scan-of { font-family: var(--mono); font-size: 0.64rem; color: var(--dim); letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 9px; }
`;

const results = `
.results { padding-block: 38px 72px; }
.crumb { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 26px; }
.crumb-file { font-family: var(--mono); font-size: 0.76rem; color: var(--ash); }
.crumb-file b { color: var(--bone); font-weight: 500; }
.back { font-family: var(--mono); font-size: 0.7rem; letter-spacing: 0.07em; text-transform: uppercase;
  background: none; border: 1px solid var(--line); color: var(--ash); padding: 8px 15px; border-radius: 3px; cursor: pointer; transition: all .15s; }
.back:hover { border-color: var(--line-2); color: var(--bone); }

/* verdict band — the page's one loud moment */
.verdict { display: grid; grid-template-columns: auto 1fr; gap: clamp(28px, 5vw, 64px); align-items: end;
  padding-bottom: 26px; border-bottom: 1px solid var(--line); margin-bottom: 30px; }
.score-wrap { display: flex; align-items: flex-end; gap: 14px; }
.score { font-family: var(--display); font-weight: 800; font-size: clamp(5.2rem, 15vw, 11rem);
  line-height: 0.78; letter-spacing: -0.06em; }
.score.sev-crit { color: var(--crit); } .score.sev-high { color: var(--high); }
.score.sev-med { color: var(--med); } .score.sev-safe { color: var(--safe); }
.score-meta { padding-bottom: 12px; }
.score-of { font-family: var(--display); font-weight: 700; font-size: 1.5rem; color: var(--dim); letter-spacing: -0.02em; }
.score-cap { font-family: var(--mono); font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.13em; color: var(--dim); margin-top: 5px; }
.verdict-right { padding-bottom: 10px; }
.verdict-count { font-family: var(--display); font-weight: 700; font-size: clamp(1.3rem, 3vw, 2rem); letter-spacing: -0.025em; margin-bottom: 16px; }
.verdict-count span { color: var(--dim); }

/* threat composition — one segment per finding, ordered by severity */
.threat { display: flex; gap: 2px; height: 30px; margin-bottom: 12px; }
.threat i { flex: 1; border-radius: 1px; transform-origin: bottom; animation: rise .5s cubic-bezier(.2,.8,.3,1) backwards; }
@keyframes rise { from { transform: scaleY(0.06); opacity: 0; } to { transform: scaleY(1); opacity: 1; } }
.threat i.s-CRITICAL { background: var(--crit); } .threat i.s-HIGH { background: var(--high); }
.threat i.s-MEDIUM { background: var(--med); } .threat i.s-LOW { background: var(--safe); }
.threat-key { display: flex; flex-wrap: wrap; gap: 18px; font-family: var(--mono); font-size: 0.68rem; color: var(--ash); }
.threat-key span { display: flex; align-items: center; gap: 7px; }
.threat-key i { width: 8px; height: 8px; border-radius: 1px; }
.threat-key b { color: var(--bone); font-weight: 500; }

/* kill chain rail */
.chain-block { border: 1px solid var(--line); border-left: 2px solid var(--crit); background: var(--panel); border-radius: 3px; padding: 20px 22px; margin-bottom: 30px; }
.chain-head { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.chain-title { font-family: var(--display); font-weight: 700; font-size: 1.1rem; letter-spacing: -0.02em; }
.chain-note { font-family: var(--mono); font-size: 0.68rem; color: var(--ash); }
.chain { display: flex; align-items: center; flex-wrap: wrap; gap: 0; }
.hop { display: flex; align-items: center; }
.hop-node { font-family: var(--mono); font-size: 0.68rem; padding: 7px 11px; border: 1px solid var(--line-2);
  background: var(--sunk); border-radius: 2px; color: var(--ash); white-space: nowrap; }
.hop:first-child .hop-node { border-color: var(--crit); color: var(--crit); }
.hop:last-child .hop-node { border-color: var(--bone); color: var(--bone); }
.hop-arrow { width: 22px; height: 1px; background: var(--line-2); position: relative; flex-shrink: 0; }
.hop-arrow::after { content: ''; position: absolute; right: 0; top: -2px; border-left: 4px solid var(--line-2); border-block: 2.5px solid transparent; }

/* layout */
.cols { display: grid; grid-template-columns: minmax(0,1fr) 400px; gap: 20px; align-items: start; }
.span { grid-column: 1 / -1; }

/* findings */
.finding { padding: 15px 18px; border-bottom: 1px solid var(--line); cursor: pointer; position: relative; transition: background .15s; }
.finding::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 2px; background: transparent; }
.finding.f-CRITICAL::before { background: var(--crit); } .finding.f-HIGH::before { background: var(--high); }
.finding.f-MEDIUM::before { background: var(--med); } .finding.f-LOW::before { background: var(--safe); }
.finding:last-child { border-bottom: none; }
.finding:hover { background: var(--panel-2); }
.finding-meta { display: flex; gap: 16px; font-family: var(--mono); font-size: 0.66rem; color: var(--dim); }
.finding-code { background: var(--sunk); border: 1px solid var(--line); border-radius: 2px; padding: 9px 11px;
  margin-top: 10px; font-family: var(--mono); font-size: 0.71rem; color: var(--ash); word-break: break-all; }
.finding-impact { font-size: 0.79rem; color: var(--ash); margin-top: 8px; line-height: 1.55; }
.finding-fix { font-size: 0.79rem; color: var(--safe); margin-top: 7px; line-height: 1.55; font-family: var(--mono); }
.empty { padding: 40px 20px; text-align: center; font-family: var(--mono); font-size: 0.8rem; color: var(--safe); }

/* intelligence panel */
.intel { position: sticky; top: 76px; }
.intel-sec { padding: 15px 18px; border-bottom: 1px solid var(--line); }
.intel-sec:last-child { border-bottom: none; }
.intel-label { font-family: var(--mono); font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.13em; color: var(--dim); margin-bottom: 11px; }
.intel-summary { font-size: 0.84rem; color: var(--ash); line-height: 1.6; }
.rec { display: flex; gap: 11px; margin-bottom: 13px; }
.rec:last-child { margin-bottom: 0; }
.rec-n { font-family: var(--mono); font-size: 0.68rem; color: var(--dim); padding-top: 1px; }
.rec-act { font-size: 0.82rem; font-weight: 500; margin-bottom: 3px; }
.rec-why { font-size: 0.75rem; color: var(--ash); line-height: 1.55; }
.rec-code { background: var(--sunk); border: 1px solid var(--line); border-radius: 2px; padding: 8px 10px;
  margin-top: 7px; font-family: var(--mono); font-size: 0.68rem; color: var(--ash); white-space: pre-wrap; word-break: break-all; }
.chip { display: inline-flex; align-items: center; gap: 7px; border: 1px solid var(--line-2);
  border-radius: 2px; padding: 4px 9px; margin: 0 4px 4px 0; font-family: var(--mono); font-size: 0.68rem; color: var(--ash); }
.chip b { color: var(--crit); font-weight: 500; }
.kv { display: flex; justify-content: space-between; gap: 12px; padding: 6px 0; border-bottom: 1px solid var(--line); font-family: var(--mono); font-size: 0.72rem; }
.kv:last-child { border-bottom: none; }
.kv span:first-child { color: var(--ash); }
.kv span:last-child { color: var(--bone); }
.dead { padding: 20px 18px; font-family: var(--mono); font-size: 0.76rem; color: var(--dim); }

footer { border-top: 1px solid var(--line); padding-block: 20px; margin-top: auto; }
.foot-in { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
  font-family: var(--mono); font-size: 0.68rem; color: var(--dim); }
`;

const guardrail = `
.guard { padding-block: 38px 72px; }
.guard-head { margin-bottom: 26px; }
.guard-title { font-family: var(--display); font-weight: 800; font-size: clamp(2rem, 5vw, 3.2rem); letter-spacing: -0.035em; line-height: 0.96; margin-bottom: 14px; }
.guard-deck { font-size: 0.94rem; color: var(--ash); max-width: 62ch; line-height: 1.65; }
.paste { width: 100%; min-height: 190px; background: var(--sunk); border: 1px solid var(--line); border-radius: 3px;
  padding: 16px 18px; font-family: var(--mono); font-size: 0.76rem; line-height: 1.7; color: var(--bone); resize: vertical; }
.paste:focus-visible { outline: none; border-color: var(--line-2); }
.paste::placeholder { color: var(--dim); }
.guard-bar { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-top: 16px; }
.ghost { font-family: var(--mono); font-size: 0.7rem; letter-spacing: 0.06em; text-transform: uppercase;
  background: none; border: 1px solid var(--line); color: var(--ash); padding: 12px 16px; border-radius: 3px; cursor: pointer; transition: all .15s; }
.ghost:hover { border-color: var(--line-2); color: var(--bone); }

/* verdict banner: the count collapsing is the whole story */
.gv { display: grid; grid-template-columns: auto 1fr; gap: clamp(24px, 4vw, 52px); align-items: center;
  border: 1px solid var(--line); border-left-width: 2px; background: var(--panel); border-radius: 3px; padding: 26px 28px; margin-block: 30px 22px; }
.gv.v-remediated { border-left-color: var(--safe); }
.gv.v-partial { border-left-color: var(--high); }
.gv.v-failed, .gv.v-blocked { border-left-color: var(--crit); }
.gv.v-clean { border-left-color: var(--safe); }
.collapse { display: flex; align-items: center; gap: 18px; }
.collapse-n { font-family: var(--display); font-weight: 800; font-size: clamp(3rem, 8vw, 5.4rem); line-height: 0.8; letter-spacing: -0.05em; }
.collapse-n.was { color: var(--crit); }
.collapse-n.now { color: var(--safe); }
.collapse-n.now.some { color: var(--high); }
.collapse-to { color: var(--dim); font-size: 1.6rem; }
.gv-verdict { font-family: var(--mono); font-size: 0.66rem; letter-spacing: 0.14em; text-transform: uppercase; color: var(--dim); margin-bottom: 8px; }
.gv-msg { font-size: 0.94rem; color: var(--bone); line-height: 1.6; }
.gv-tally { display: flex; gap: 20px; flex-wrap: wrap; margin-top: 13px; font-family: var(--mono); font-size: 0.7rem; color: var(--ash); }
.gv-tally b { font-weight: 500; }
.gv-tally .t-res b { color: var(--safe); } .gv-tally .t-rem b { color: var(--high); } .gv-tally .t-new b { color: var(--crit); }

/* split diff */
.diff { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.diff-pane { border: 1px solid var(--line); background: var(--panel); border-radius: 3px; overflow: hidden; display: flex; flex-direction: column; }
.diff-head { padding: 11px 16px; border-bottom: 1px solid var(--line); display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.diff-label { font-family: var(--mono); font-size: 0.66rem; letter-spacing: 0.13em; text-transform: uppercase; color: var(--ash); }
.diff-tag { font-family: var(--mono); font-size: 0.62rem; padding: 2px 7px; border-radius: 2px; letter-spacing: 0.08em; text-transform: uppercase; }
.diff-tag.bad { background: var(--crit-dim); color: var(--crit); }
.diff-tag.good { background: var(--safe-dim); color: var(--safe); }
.code { margin: 0; overflow-x: auto; font-family: var(--mono); font-size: 0.72rem; line-height: 1.75; flex: 1; }
.code-row { display: grid; grid-template-columns: 44px 1fr; }
.code-row.hit { background: var(--crit-dim); }
.code-row.hit .code-ln { color: var(--crit); }
.code-ln { text-align: right; padding-right: 12px; color: var(--dim); user-select: none; border-right: 1px solid var(--line); }
.code-tx { padding-left: 14px; white-space: pre; color: var(--ash); padding-right: 14px; }
.code-row.hit .code-tx { color: var(--bone); }

@media (max-width: 900px) {
  .cols { grid-template-columns: 1fr; }
  .intel { position: static; }
  .diff { grid-template-columns: 1fr; }
  .verdict { grid-template-columns: 1fr; align-items: start; gap: 24px; }
}
@media (max-width: 620px) {
  .shell { padding-inline: 18px; }
  .slab { grid-template-columns: 1fr; gap: 20px; }
  .go { width: 100%; }
  .tabs .tab { padding: 7px 9px; }
  .metric { padding-right: 20px; margin-right: 20px; }
  .gv { grid-template-columns: 1fr; gap: 20px; }
}
@media (prefers-reduced-motion: reduce) {
  * { animation: none !important; transition: none !important; }
}
`;

const plan = `
.plan { padding-block: 38px 72px; }
.plan-title { font-family: var(--display); font-weight: 800; font-size: clamp(2rem, 5vw, 3.2rem); letter-spacing: -0.035em; line-height: 0.96; margin-bottom: 14px; }
.plan-deck { font-size: 0.94rem; color: var(--ash); max-width: 64ch; line-height: 1.65; margin-bottom: 30px; }
.slots { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 18px; }
.slot { border: 1px solid var(--line); background: var(--panel); border-radius: 3px; padding: 22px; cursor: pointer; transition: border-color .2s, background .2s; }
.slot:hover, .slot.over { border-color: var(--line-2); background: var(--panel-2); }
.slot.over { border-style: dashed; }
.slot.filled { border-color: var(--safe); }
.slot-req { font-family: var(--mono); font-size: 0.6rem; letter-spacing: 0.13em; text-transform: uppercase; color: var(--dim); margin-bottom: 9px; }
.slot.filled .slot-req { color: var(--safe); }
.slot-name { font-family: var(--display); font-weight: 700; font-size: 1.02rem; letter-spacing: -0.02em; margin-bottom: 6px; word-break: break-all; }
.slot-hint { font-family: var(--mono); font-size: 0.7rem; color: var(--dim); line-height: 1.6; }

/* the comparison is the thesis */
.versus { display: grid; grid-template-columns: 1fr auto 1fr; gap: clamp(16px, 4vw, 44px); align-items: center;
  border: 1px solid var(--line); background: var(--panel); border-radius: 3px; padding: 28px; margin-block: 30px 20px; }
.vs-side { text-align: center; }
.vs-n { font-family: var(--display); font-weight: 800; font-size: clamp(2.8rem, 7vw, 4.6rem); line-height: 0.82; letter-spacing: -0.05em; }
.vs-n.weak { color: var(--dim); }
.vs-n.strong { color: var(--crit); }
.vs-label { font-family: var(--mono); font-size: 0.64rem; letter-spacing: 0.13em; text-transform: uppercase; color: var(--ash); margin-top: 12px; }
.vs-sub { font-family: var(--mono); font-size: 0.66rem; color: var(--dim); margin-top: 5px; }
.vs-mid { font-family: var(--mono); font-size: 0.64rem; letter-spacing: 0.12em; text-transform: uppercase; color: var(--dim); text-align: center; }
.vs-note { font-size: 0.84rem; color: var(--ash); line-height: 1.6; border-left: 2px solid var(--line-2); padding-left: 16px; margin-bottom: 22px; }

.vex { margin-top: 20px; }
.vex-body { background: var(--sunk); border-top: 1px solid var(--line); padding: 16px 18px; overflow-x: auto;
  font-family: var(--mono); font-size: 0.7rem; line-height: 1.7; color: var(--ash); white-space: pre; max-height: 420px; overflow-y: auto; }
.copy { font-family: var(--mono); font-size: 0.64rem; letter-spacing: 0.08em; text-transform: uppercase;
  background: none; border: 1px solid var(--line); color: var(--ash); padding: 5px 11px; border-radius: 2px; cursor: pointer; transition: all .15s; }
.copy:hover { border-color: var(--line-2); color: var(--bone); }
.copy.done { border-color: var(--safe); color: var(--safe); }
@media (max-width: 760px) {
  .slots { grid-template-columns: 1fr; }
  .versus { grid-template-columns: 1fr; gap: 20px; }
  .vs-mid { display: none; }
}
`;

const graph = `
.attack-graph-panel { grid-column: 1 / -1; margin-top: 4px; }
.attack-path-badge { font-family: var(--mono); font-size: 0.64rem; padding: 3px 9px; border-radius: 2px;
  letter-spacing: 0.09em; text-transform: uppercase; }
.attack-path-badge.danger { background: var(--crit-dim); color: var(--crit); }
.attack-path-badge.safe { background: var(--safe-dim); color: var(--safe); }
.attack-graph-empty { padding: 30px 18px; font-family: var(--mono); font-size: 0.76rem; color: var(--dim); line-height: 1.6; }
.attack-path-list { border-bottom: 1px solid var(--line); }
.attack-path-item { padding: 15px 18px; border-bottom: 1px solid var(--line); position: relative; }
.attack-path-item::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 2px; background: var(--crit); }
.attack-path-item:last-child { border-bottom: none; }
.attack-path-narrative { font-size: 0.81rem; color: var(--ash); line-height: 1.6; margin-top: 6px; }
.attack-path-chain { font-family: var(--mono); font-size: 0.7rem; color: var(--bone); margin-top: 10px;
  background: var(--sunk); border: 1px solid var(--line); border-radius: 2px; padding: 9px 11px; word-break: break-word; line-height: 1.7; }
.attack-graph-canvas { background: radial-gradient(circle at 50% 45%, rgba(143,135,153,0.07), transparent 70%); }
.attack-graph-canvas svg { display: block; }
.attack-graph-canvas text { pointer-events: none; user-select: none; }
.attack-graph-legend { display: flex; flex-wrap: wrap; gap: 20px; padding: 13px 18px; border-top: 1px solid var(--line);
  font-family: var(--mono); font-size: 0.66rem; color: var(--ash); }
.attack-graph-legend span { display: flex; align-items: center; gap: 7px; }
.attack-graph-legend i { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.attack-graph-legend .legend-path i { width: 14px; height: 2px; border-radius: 0; background: var(--crit); }
`;

const API_URL = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");
const SEV_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function useCountUp(target, ms = 850) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const goal = Number(target) || 0;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setN(goal); return; }
    let raf, t0;
    const tick = (t) => {
      if (!t0) t0 = t;
      const p = Math.min((t - t0) / ms, 1);
      setN(Math.round(goal * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return n;
}

function Mark({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <path d="M20 3 L34 9.5 V20.5 C34 28.5 27.6 34.6 20 37.5 C12.4 34.6 6 28.5 6 20.5 V9.5 Z"
            fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M6 17 H34" stroke="currentColor" strokeWidth="1" opacity="0.35" />
      <path d="M20 3 V37.5" stroke="currentColor" strokeWidth="1" opacity="0.35" />
      <circle cx="20" cy="17" r="3.1" fill="currentColor" />
    </svg>
  );
}

// ─── NAV ──────────────────────────────────────────────────────────────────────
function Nav({ page, onNav }) {
  return (
    <nav>
      <div className="shell nav-in">
        <div style={{ display: "flex", alignItems: "center", gap: 26 }}>
          <button className="brand" onClick={() => onNav("upload")}>
            <Mark size={24} />
            <span className="brand-name">TerraShield</span>
          </button>
          <div className="tabs">
            <button className={`tab${page === "upload" || page === "results" ? " on" : ""}`} onClick={() => onNav("upload")}>Scan</button>
            <button className={`tab${page === "guardrail" ? " on" : ""}`} onClick={() => onNav("guardrail")}>Guardrail</button>
            <button className={`tab${page === "plan" ? " on" : ""}`} onClick={() => onNav("plan")}>Plan</button>
          </div>
        </div>
        <span className="pulse"><i />Live</span>
      </div>
    </nav>
  );
}

// ─── UPLOAD ───────────────────────────────────────────────────────────────────
function UploadPage({ onScan }) {
  const [file, setFile] = useState(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");
  const [stepN, setStepN] = useState(0);
  const inputRef = useRef();

  const take = (f) => {
    if (f && f.name.endsWith(".tf")) setFile(f);
    else if (f) alert("TerraShield reads Terraform. Choose a .tf file.");
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setOver(false);
    take(e.dataTransfer.files[0]);
  }, []);

  const run = async () => {
    if (!file) return;
    setBusy(true);
    const steps = [
      "Parsing HCL resource blocks",
      "Running rule-based security checks",
      "Tracing multi-hop attack paths",
      "Querying NIST CVE database",
      "Checking module registry and pinning",
      "Running AI analysis",
      "Compiling results",
    ];
    setStep(steps[0]); setStepN(0);
    let i = 0;
    const timer = setInterval(() => {
      i = Math.min(i + 1, steps.length - 1);
      setStep(steps[i]); setStepN(i);
    }, 1700);

    try {
      const a = new FormData(); a.append("file", file);
      const b = new FormData(); b.append("file", file);
      const [scanRes, graphRes] = await Promise.allSettled([
        fetch(`${API_URL}/api/scan`, { method: "POST", body: a }),
        fetch(`${API_URL}/api/attack-graph`, { method: "POST", body: b }),
      ]);
      clearInterval(timer);
      if (scanRes.status !== "fulfilled" || !scanRes.value.ok) {
        const err = scanRes.status === "fulfilled" ? await scanRes.value.json().catch(() => ({})) : {};
        throw new Error(err?.detail?.message || "The scan request did not complete");
      }
      const data = await scanRes.value.json();
      const graph = graphRes.status === "fulfilled" && graphRes.value.ok ? await graphRes.value.json() : null;
      setBusy(false);
      onScan(file.name, data, graph);
    } catch (e) {
      clearInterval(timer);
      setBusy(false);
      alert(`Scan failed: ${e.message}`);
    }
  };

  return (
    <div className="shell stage">
      <div className="kicker">Infrastructure as Code security</div>
      <h1 className="headline">Find the path<br /><em>before they do.</em></h1>
      <p className="deck">
        TerraShield reads your Terraform, flags what is misconfigured, and traces how an
        attacker would chain those mistakes together — from an open port all the way to your data.
      </p>

      <div className="metrics">
        <div className="metric"><div className="metric-n tnum">12</div><div className="metric-l">Security rules</div></div>
        <div className="metric"><div className="metric-n tnum">6</div><div className="metric-l">Hop path depth</div></div>
        <div className="metric"><div className="metric-n tnum">4</div><div className="metric-l">Live data sources</div></div>
        <div className="metric"><div className="metric-n">AI</div><div className="metric-l">Remediation</div></div>
      </div>

      <div
        className={`slab${over ? " over" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        onClick={() => !busy && inputRef.current?.click()}
      >
        {busy && (
          <div className="scanning">
            <div className="scan-rail"><i /></div>
            <div className="scan-of">Scanning · step {stepN + 1} of 7</div>
            <div className="scan-step">{step}</div>
          </div>
        )}
        <input ref={inputRef} type="file" accept=".tf" style={{ display: "none" }} onChange={(e) => take(e.target.files[0])} />
        <div>
          <div className="slab-lead">{file ? file.name : "Drop a Terraform file"}</div>
          <div className="slab-sub">{file ? `${(file.size / 1024).toFixed(1)} KB · ready to scan` : "or click to browse · .tf only"}</div>
          {file && <div className="slab-file">✓ File loaded</div>}
        </div>
        <button className="go" disabled={!file || busy} onClick={(e) => { e.stopPropagation(); run(); }}>
          {busy ? "Scanning…" : "Run scan →"}
        </button>
      </div>
    </div>
  );
}

// ─── RESULTS ──────────────────────────────────────────────────────────────────
function ThreatBar({ findings }) {
  const sorted = [...findings].sort((a, b) => SEV_ORDER.indexOf(a.severity) - SEV_ORDER.indexOf(b.severity));
  return (
    <div className="threat" role="img" aria-label={`${findings.length} findings by severity`}>
      {sorted.map((f, i) => (
        <i key={i} className={`s-${f.severity}`} style={{ animationDelay: `${Math.min(i * 38, 700)}ms` }} />
      ))}
    </div>
  );
}

function Verdict({ findings, ai }) {
  const raw = ai ? (ai.risk_score ?? ai.overall_risk_score) : null;
  const score = Number(raw);
  const hasScore = raw != null && !Number.isNaN(score);
  const shown = useCountUp(hasScore ? score : 0);
  const total = useCountUp(findings.length);
  const counts = SEV_ORDER.reduce((a, s) => ({ ...a, [s]: findings.filter((f) => f.severity === s).length }), {});
  const tone = !hasScore ? "" : score >= 7 ? "sev-crit" : score >= 4 ? "sev-high" : score > 0 ? "sev-med" : "sev-safe";

  return (
    <div className="verdict">
      <div className="score-wrap">
        <div className={`score tnum ${tone}`}>{hasScore ? shown : "—"}</div>
        <div className="score-meta">
          <div className="score-of">/10</div>
          <div className="score-cap">Risk score</div>
        </div>
      </div>
      <div className="verdict-right">
        <div className="verdict-count tnum">
          {total} finding{findings.length === 1 ? "" : "s"} <span>across {new Set(findings.map((f) => f.category)).size} categories</span>
        </div>
        <ThreatBar findings={findings} />
        <div className="threat-key">
          {SEV_ORDER.map((s) => (
            <span key={s}>
              <i style={{ background: `var(--${s === "CRITICAL" ? "crit" : s === "HIGH" ? "high" : s === "MEDIUM" ? "med" : "safe"})` }} />
              <b className="tnum">{counts[s]}</b> {s.toLowerCase()}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function KillChain({ graph }) {
  const paths = graph?.attack_paths || [];
  if (!paths.length) return null;
  const path = paths[0];
  const short = (id) => id.split(".").slice(-1)[0];
  return (
    <div className="chain-block">
      <div className="chain-head">
        <span className="chain-title">Reachable path to sensitive data</span>
        <span className="chain-note">
          {path.length - 1} hops · {paths.length} path{paths.length === 1 ? "" : "s"} found
        </span>
      </div>
      <div className="chain">
        {path.map((node, i) => (
          <div className="hop" key={node}>
            <span className="hop-node" title={node}>{short(node)}</span>
            {i < path.length - 1 && <span className="hop-arrow" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function Finding({ f, i }) {
  const [open, setOpen] = useState(i < 2);
  return (
    <div className={`finding f-${f.severity}`} onClick={() => setOpen(!open)}>
      <div className="finding-top">
        <span className={`sev-badge badge-${f.severity}`}>{f.severity}</span>
        <span className="finding-title">{f.title}</span>
      </div>
      <div className="finding-meta">
        <span>Line {f.line}</span>
        <span>{f.category}</span>
      </div>
      {open && (
        <>
          {f.code && <div className="finding-code">{f.code}</div>}
          {f.impact && <div className="finding-impact">{f.impact}</div>}
          {f.fix && <div className="finding-fix">→ {f.fix}</div>}
        </>
      )}
    </div>
  );
}

function Intel({ ai }) {
  const cves = ai.mcp_cve?.length ? ai.mcp_cve : ai.cve_findings?.length ? ai.cve_findings : [];
  const costs = ai.mcp_pricing?.length ? ai.mcp_pricing : ai.cost_estimates?.length ? ai.cost_estimates : [];
  const modules = (ai.mcp_registry || ai.module_findings || []).filter((m) => m?.is_outdated);
  const policies = (ai.mcp_policy?.length ? ai.mcp_policy : ai.policy_violations || []).filter((p) => p && typeof p === "object" && p.title);

  return (
    <div className="findings-panel intel">
      <div className="panel-header"><span className="panel-title">Intelligence</span></div>
      {ai.summary && (
        <div className="intel-sec">
          <div className="intel-label">Assessment</div>
          <div className="intel-summary">{ai.summary}</div>
        </div>
      )}
      {ai.priority_recommendations?.length > 0 && (
        <div className="intel-sec">
          <div className="intel-label">Priority actions</div>
          {ai.priority_recommendations.map((r, i) => (
            <div className="rec" key={i}>
              <span className="rec-n tnum">{String(r.priority ?? i + 1).padStart(2, "0")}</span>
              <div>
                <div className="rec-act">{r.action}</div>
                {r.reason && <div className="rec-why">{r.reason}</div>}
                {r.fix_code && <div className="rec-code">{r.fix_code}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {cves.length > 0 && (
        <div className="intel-sec">
          <div className="intel-label">CVEs · NIST NVD</div>
          {cves.map((c, i) => (
            <span className="chip" key={i}><b>{c.cve_id}</b> CVSS {c.cvss_score}</span>
          ))}
        </div>
      )}
      {policies.length > 0 && (
        <div className="intel-sec">
          <div className="intel-label">Policy violations</div>
          {policies.map((p, i) => <span className="chip" key={i}>[{p.severity}] {p.title}</span>)}
        </div>
      )}
      {modules.length > 0 && (
        <div className="intel-sec">
          <div className="intel-label">Outdated modules</div>
          {modules.map((m, i) => (
            <div className="kv" key={i}>
              <span>{m.module || m.module_source}</span>
              <span>v{m.current_version} → v{m.latest_version}</span>
            </div>
          ))}
        </div>
      )}
      {costs.length > 0 && (
        <div className="intel-sec">
          <div className="intel-label">Monthly cost impact</div>
          {costs.map((c, i) => {
            const v = c.estimated_monthly_cost_usd;
            return (
              <div className="kv" key={i}>
                <span>{c.resource || c.service || c.name || "Resource"}</span>
                <span>{v == null ? "—" : v === 0 ? "Free tier" : `$${v}`}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ResultsPage({ filename, scanData, attackGraph, onReset }) {
  const findings = scanData?.findings || [];
  const ai = scanData?.ai_analysis && scanData.ai_analysis.analysis_available !== false ? scanData.ai_analysis : null;

  return (
    <div className="shell results">
      <div className="crumb">
        <div className="crumb-file">Scanned <b>{filename}</b></div>
        <button className="back" onClick={onReset}>← New scan</button>
      </div>

      <Verdict findings={findings} ai={ai} />
      <KillChain graph={attackGraph} />

      <div className="cols">
        <div className="findings-panel">
          <div className="panel-header">
            <span className="panel-title">Findings</span>
            <span className="panel-title">{findings.length} total · click to expand</span>
          </div>
          {findings.length === 0
            ? <div className="empty">No issues found in this configuration.</div>
            : findings.map((f, i) => <Finding key={i} f={f} i={i} />)}
        </div>

        {ai ? <Intel ai={ai} /> : (
          <div className="findings-panel intel">
            <div className="panel-header"><span className="panel-title">Intelligence</span></div>
            <div className="dead">AI analysis unavailable for this scan.</div>
          </div>
        )}

        <AttackGraph graphData={attackGraph} error={attackGraph === null} />
      </div>
    </div>
  );
}

// ─── GUARDRAIL ────────────────────────────────────────────────────────────────
const SAMPLE = `provider "aws" {
  region     = "ap-south-1"
  access_key = "AKIAIOSFODNN7EXAMPLE"
  secret_key = "wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
}

resource "aws_security_group" "web" {
  name = "web-sg"

  ingress {
    from_port   = 22
    to_port     = 22
    protocol    = "tcp"
    cidr_blocks = ["0.0.0.0/0"]
  }
}

resource "aws_s3_bucket" "uploads" {
  bucket = "company-user-uploads"
  acl    = "public-read"
}

resource "aws_db_instance" "app" {
  identifier          = "app-db"
  engine              = "postgres"
  username            = "admin"
  password            = "Password123!"
  publicly_accessible = true
  storage_encrypted   = false
  skip_final_snapshot = true
}`;

function CodePane({ label, tag, tagTone, code, hitLines }) {
  const lines = (code || "").split("\n");
  const hits = new Set(hitLines || []);
  return (
    <div className="diff-pane">
      <div className="diff-head">
        <span className="diff-label">{label}</span>
        {tag && <span className={`diff-tag ${tagTone}`}>{tag}</span>}
      </div>
      <div className="code">
        {lines.map((ln, i) => (
          <div className={`code-row${hits.has(i + 1) ? " hit" : ""}`} key={i}>
            <span className="code-ln tnum">{i + 1}</span>
            <span className="code-tx">{ln || " "}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuardrailPage() {
  const [code, setCode] = useState(SAMPLE);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!code.trim()) return;
    setBusy(true); setErr(""); setRes(null);
    try {
      const r = await fetch(`${API_URL}/api/mcp/guardrail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, source: "terrashield-web" }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.detail?.message || `Request failed (${r.status})`);
      }
      setRes(await r.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const before = res ? res.findings_before.length : 0;
  const after = res ? res.findings_after.length : 0;
  const beforeN = useCountUp(before);
  const afterN = useCountUp(after);

  return (
    <div className="shell guard">
      <div className="guard-head">
        <h1 className="guard-title">Guardrail</h1>
        <p className="guard-deck">
          AI agents now write infrastructure directly. Paste what an agent produced —
          TerraShield scans it, rewrites what violates policy, then re-scans its own
          output so the verdict reflects what was actually fixed, not what the model claimed.
        </p>
      </div>

      <textarea
        id="guardrail-input"
        className="paste"
        value={code}
        spellCheck="false"
        onChange={(e) => setCode(e.target.value)}
        placeholder="Paste agent-generated Terraform here…"
      />
      <div className="guard-bar">
        <button className="go" disabled={busy || !code.trim()} onClick={submit}>
          {busy ? "Checking…" : "Check and remediate →"}
        </button>
        <button className="ghost" onClick={() => { setCode(SAMPLE); setRes(null); setErr(""); }}>Reset sample</button>
        {err && <span style={{ fontFamily: "var(--mono)", fontSize: "0.72rem", color: "var(--crit)" }}>{err}</span>}
      </div>

      {res && (
        <>
          <div className={`gv v-${res.verdict}`}>
            <div className="collapse">
              <span className="collapse-n was tnum">{beforeN}</span>
              <span className="collapse-to">→</span>
              <span className={`collapse-n now tnum${after > 0 ? " some" : ""}`}>{afterN}</span>
            </div>
            <div>
              <div className="gv-verdict">{res.verdict}</div>
              <div className="gv-msg">{res.message}</div>
              {res.blocked && (
                <div className="gv-tally">
                  <span className="t-res"><b className="tnum">{res.resolved.length}</b> resolved</span>
                  <span className="t-rem"><b className="tnum">{res.remaining.length}</b> still present</span>
                  <span className="t-new"><b className="tnum">{res.introduced.length}</b> introduced</span>
                </div>
              )}
            </div>
          </div>

          {res.sanitized_code && (
            <div className="diff">
              <CodePane
                label="Agent output"
                tag={`${before} violation${before === 1 ? "" : "s"}`}
                tagTone="bad"
                code={res.original_code}
                hitLines={res.findings_before.map((f) => f.line)}
              />
              <CodePane
                label="After guardrail"
                tag={after === 0 ? "clean" : `${after} remaining`}
                tagTone={after === 0 ? "good" : "bad"}
                code={res.sanitized_code}
                hitLines={res.findings_after.map((f) => f.line)}
              />
            </div>
          )}

          {res.blocked && !res.sanitized_code && (
            <div className="findings-panel">
              <div className="panel-header"><span className="panel-title">Violations · not remediated</span></div>
              {res.findings_before.map((f, i) => <Finding key={i} f={f} i={i} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── PLAN SCANNER ─────────────────────────────────────────────────────────────
function Slot({ req, accept, file, onFile, hint }) {
  const [over, setOver] = useState(false);
  const ref = useRef();
  const take = (f) => {
    if (!f) return;
    if (!f.name.endsWith(accept)) { alert(`Expected a ${accept} file`); return; }
    onFile(f);
  };
  return (
    <div
      className={`slot${over ? " over" : ""}${file ? " filled" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files[0]); }}
      onClick={() => ref.current?.click()}
    >
      <input ref={ref} type="file" accept={accept} style={{ display: "none" }} onChange={(e) => take(e.target.files[0])} />
      <div className="slot-req">{file ? "✓ loaded" : req}</div>
      <div className="slot-name">{file ? file.name : `Drop ${accept}`}</div>
      <div className="slot-hint">{hint}</div>
    </div>
  );
}

function VexPanel({ vex }) {
  const [copied, setCopied] = useState(false);
  const text = JSON.stringify(vex, null, 2);
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* clipboard blocked; the document is still readable below */ }
  };
  return (
    <div className="findings-panel vex">
      <div className="panel-header">
        <span className="panel-title">OpenVEX · {vex.statements.length} statements</span>
        <button className={`copy${copied ? " done" : ""}`} onClick={copy}>{copied ? "copied" : "copy"}</button>
      </div>
      <div className="vex-body">{text}</div>
    </div>
  );
}

function PlanPage() {
  const [planFile, setPlanFile] = useState(null);
  const [srcFile, setSrcFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [staticRes, setStaticRes] = useState(null);
  const [err, setErr] = useState("");

  const run = async () => {
    if (!planFile) return;
    setBusy(true); setErr(""); setRes(null); setStaticRes(null);
    try {
      const pf = new FormData(); pf.append("file", planFile);
      const calls = [fetch(`${API_URL}/api/scan/plan`, { method: "POST", body: pf })];
      if (srcFile) {
        const sf = new FormData(); sf.append("file", srcFile);
        // Rule-based only, so both sides are measured the same way.
        calls.push(fetch(`${API_URL}/api/scan?ai=false`, { method: "POST", body: sf }));
      }
      const [planR, srcR] = await Promise.allSettled(calls);
      if (planR.status !== "fulfilled" || !planR.value.ok) {
        const d = planR.status === "fulfilled" ? await planR.value.json().catch(() => ({})) : {};
        throw new Error(d?.detail?.message || "Plan scan failed");
      }
      setRes(await planR.value.json());
      if (srcR && srcR.status === "fulfilled" && srcR.value.ok) setStaticRes(await srcR.value.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const planN = useCountUp(res ? res.total_issues : 0);
  const staticN = useCountUp(staticRes ? staticRes.total_issues : 0);

  return (
    <div className="shell plan">
      <h1 className="plan-title">Plan scanner</h1>
      <p className="plan-deck">
        A Terraform plan carries resolved values, not expressions. That is the difference between
        reading <code style={{ fontFamily: "var(--mono)", fontSize: "0.86em" }}>publicly_accessible = var.enable_public</code> and
        knowing it comes out <code style={{ fontFamily: "var(--mono)", fontSize: "0.86em" }}>true</code>. Add the source file
        alongside to see what scanning the text alone would have concluded.
      </p>

      <div className="slots">
        <Slot
          req="Required"
          accept=".json"
          file={planFile}
          onFile={setPlanFile}
          hint="Output of: terraform show -json plan.tfplan"
        />
        <Slot
          req="Optional · for comparison"
          accept=".tf"
          file={srcFile}
          onFile={setSrcFile}
          hint="The source these values came from"
        />
      </div>

      <div className="guard-bar">
        <button className="go" disabled={!planFile || busy} onClick={run}>
          {busy ? "Scanning…" : "Scan plan →"}
        </button>
        {(planFile || srcFile) && (
          <button className="ghost" onClick={() => { setPlanFile(null); setSrcFile(null); setRes(null); setStaticRes(null); setErr(""); }}>
            Clear
          </button>
        )}
        {err && <span style={{ fontFamily: "var(--mono)", fontSize: "0.72rem", color: "var(--crit)" }}>{err}</span>}
      </div>

      {res && staticRes && (
        <>
          <div className="versus">
            <div className="vs-side">
              <div className="vs-n weak tnum">{staticN}</div>
              <div className="vs-label">Static HCL</div>
              <div className="vs-sub">{srcFile?.name}</div>
            </div>
            <div className="vs-mid">versus</div>
            <div className="vs-side">
              <div className="vs-n strong tnum">{planN}</div>
              <div className="vs-label">Resolved plan</div>
              <div className="vs-sub">{planFile?.name}</div>
            </div>
          </div>
          <p className="vs-note">
            Both passes ran the same rule set with no AI. The static pass matches literals in the
            source; where a value is supplied by a variable there is no literal to match, so it has
            nothing to evaluate. The plan pass reads what those expressions actually resolve to —
            and sees inside child modules, which the source pass never opens.
          </p>
        </>
      )}

      {res && !staticRes && (
        <div className="versus" style={{ gridTemplateColumns: "1fr" }}>
          <div className="vs-side">
            <div className="vs-n strong tnum">{planN}</div>
            <div className="vs-label">Findings in resolved plan</div>
            <div className="vs-sub">terraform {res.terraform_version || "—"} · {res.resources_scanned || 0} resources affected</div>
          </div>
        </div>
      )}

      {res && (
        <div className="cols">
          <div className="findings-panel">
            <div className="panel-header">
              <span className="panel-title">Plan findings</span>
              <span className="panel-title">{res.total_issues} total</span>
            </div>
            {res.total_issues === 0
              ? <div className="empty">No issues in the resolved plan.</div>
              : res.findings.map((f, i) => (
                  <div className={`finding f-${f.severity}`} key={i}>
                    <div className="finding-top">
                      <span className={`sev-badge badge-${f.severity}`}>{f.severity}</span>
                      <span className="finding-title">{f.title}</span>
                    </div>
                    <div className="finding-meta">
                      <span>{f.address}</span>
                      <span>{f.category}</span>
                    </div>
                    <div className="finding-code">{f.code}</div>
                    <div className="finding-impact">{f.impact}</div>
                    <div className="finding-fix">→ {f.fix}</div>
                  </div>
                ))}
          </div>

          {staticRes && (
            <div className="findings-panel intel">
              <div className="panel-header">
                <span className="panel-title">Static pass</span>
                <span className="panel-title">{staticRes.total_issues}</span>
              </div>
              {staticRes.total_issues === 0
                ? <div className="dead">The source file alone yielded nothing.</div>
                : staticRes.findings.map((f, i) => (
                    <div className="intel-sec" key={i}>
                      <div className="finding-top">
                        <span className={`sev-badge badge-${f.severity}`}>{f.severity}</span>
                        <span className="finding-title" style={{ fontSize: "0.84rem" }}>{f.title}</span>
                      </div>
                      <div className="finding-meta">Line {f.line}</div>
                    </div>
                  ))}
            </div>
          )}

          {res.openvex && <div className="span"><VexPanel vex={res.openvex} /></div>}
        </div>
      )}
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("upload");
  const [filename, setFilename] = useState("");
  const [scanData, setScanData] = useState(null);
  const [attackGraph, setAttackGraph] = useState(null);

  const onScan = (name, data, graph) => {
    setFilename(name); setScanData(data); setAttackGraph(graph); setPage("results");
  };
  const onNav = (p) => {
    if (p === "upload") { setFilename(""); setScanData(null); setAttackGraph(null); }
    setPage(p);
  };

  return (
    <>
      <style>{base + upload + results + guardrail + plan + graph}</style>
      <div className="veil" />
      <div className="app">
        <Nav page={page} onNav={onNav} />
        <main>
          {page === "upload" && <UploadPage onScan={onScan} />}
          {page === "results" && (
            <ResultsPage filename={filename} scanData={scanData} attackGraph={attackGraph} onReset={() => onNav("upload")} />
          )}
          {page === "guardrail" && <GuardrailPage />}
          {page === "plan" && <PlanPage />}
        </main>
        <footer>
          <div className="shell foot-in">
            <span>TerraShield · AWS Lambda ap-south-1 · Groq · Cloudflare Pages</span>
            <span>terrashield.dev</span>
          </div>
        </footer>
      </div>
    </>
  );
}
